import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useState } from "react";
import { styled, Link as Link$1, Alert as Alert$1, Fade, AlertTitle } from "@mui/material";
import { OpenInNewOutlined } from "@mui/icons-material";
import "cartocolor";
import { c as ICON_SIZE_SMALL, b as ICON_SIZE_MEDIUM, u as useTranslationWithFallback, T as Typography } from "./css-utils-BHYA7By_.js";
const ScreenReaderOnly = styled("span")(() => ({
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0
}));
const StyledLink = styled(Link$1)(() => ({
  display: "inline-block"
}));
const Icon = styled("span", {
  shouldForwardProp: (prop) => prop !== "variant" && prop !== "iconPosition"
})(
  ({ variant, iconPosition }) => ({
    display: "inline-flex",
    position: "relative",
    top: "calc(1em * 5/32)",
    // heuristic to align icon with true perceived baseline (25% offset ratio)
    ...iconPosition === "right" && {
      marginLeft: "0.25em"
    },
    ...iconPosition === "left" && {
      marginRight: "0.25em"
    },
    svg: {
      // Size adapts to the text size, but make sure the icon size is at least 12px
      fontSize: `max(1em, ${ICON_SIZE_SMALL})`,
      color: "inherit !important",
      "> path": {
        color: "inherit !important"
      }
    },
    // Caption and undefined variants use smaller offset and smaller icon
    ...variant === "caption" && {
      top: "calc(1em * 1/5)",
      // 20% offset ratio
      svg: {
        fontSize: ICON_SIZE_SMALL
      }
    },
    // Button, body2 and body1 variants use medium-sized icons
    ...(variant === "button" || variant === "body2" || variant === "body1") && {
      top: "calc(1em * 1/4)",
      // 25% offset ratio
      svg: {
        fontSize: ICON_SIZE_MEDIUM
      }
    }
  })
);
function _Link({
  children,
  variant,
  startIcon,
  endIcon,
  color,
  external,
  showExternalIcon = true,
  screenReaderText,
  fontWeight = 500,
  target,
  rel,
  "aria-describedby": ariaDescribedby,
  ...otherProps
}, ref) {
  const screenReaderTextValue = useTranslationWithFallback(
    "c4r.button.opensInNewTab",
    screenReaderText
  );
  const isButtonLink = otherProps.component === "button";
  return /* @__PURE__ */ jsxs(
    StyledLink,
    {
      ...otherProps,
      variant,
      fontWeight,
      "data-color": color,
      "data-name": isButtonLink ? "button-link" : "link",
      role: isButtonLink ? "button" : "link",
      color,
      ref,
      target: external ? "_blank" : target,
      rel: external ? "noopener noreferrer" : rel,
      "aria-describedby": external ? "external-hint" : ariaDescribedby,
      children: [
        startIcon && /* @__PURE__ */ jsx(Icon, { variant, iconPosition: "left", children: startIcon }),
        children,
        (endIcon || external && showExternalIcon) && /* @__PURE__ */ jsx(Icon, { variant, iconPosition: "right", children: endIcon || /* @__PURE__ */ jsx(OpenInNewOutlined, {}) }),
        external && /* @__PURE__ */ jsx(ScreenReaderOnly, { id: "external-hint", children: screenReaderTextValue })
      ]
    }
  );
}
const Link = forwardRef(_Link);
const StyledAlert = styled(Alert$1, {
  shouldForwardProp: (prop) => ![
    "content",
    "hasCloseButton",
    "hasAction",
    "hasTitle",
    "isSticky"
  ].includes(prop)
})(
  ({ content, hasCloseButton, hasAction, hasTitle, isSticky, theme }) => ({
    columnGap: theme.spacing(1),
    minHeight: theme.spacing(6),
    ...hasAction && {
      display: "grid",
      gridTemplateAreas: content === "inline" || hasCloseButton ? `"icon message actions"` : `
    "icon message"
    "icon actions"
    `,
      gridTemplateColumns: hasCloseButton ? `${ICON_SIZE_MEDIUM} 1fr ${theme.spacing(3)}` : `${ICON_SIZE_MEDIUM}`
    },
    ...isSticky && {
      borderRadius: 0
    },
    ".MuiAlert-message": {
      flex: 1,
      paddingTop: hasTitle ? theme.spacing(0.25) : theme.spacing(0.5),
      "& a": {
        color: theme.palette.primary.main,
        textDecoration: "none",
        "&:hover": {
          textDecoration: "underline"
        }
      },
      ...hasAction && {
        gridArea: "message"
      }
    },
    ".MuiAlert-icon": {
      height: hasTitle ? theme.spacing(2.5) : theme.spacing(3),
      marginRight: 0,
      ...hasAction && {
        gridArea: "icon"
      }
    },
    ".MuiAlert-action": {
      alignItems: content === "inline" && !hasCloseButton ? "center" : "flex-start",
      margin: content === "block" && !hasCloseButton ? theme.spacing(1.5, 0, 0.5) : 0,
      marginLeft: content === "inline" || hasCloseButton ? "auto" : 0,
      padding: 0,
      ...hasAction && {
        gridArea: "actions"
      },
      ".MuiIconButton-root svg path": {
        fill: hasCloseButton ? theme.palette.text.secondary : void 0
      }
    }
  })
);
function _Alert({
  title,
  action,
  severity = "neutral",
  content = "inline",
  variant = "standard",
  children,
  onClose,
  open: controlledOpen,
  isSticky,
  ...otherProps
}, ref) {
  const [open, setOpen] = useState(controlledOpen ?? true);
  const handleClose = onClose ? (ev) => {
    onClose(ev);
    setOpen(false);
  } : void 0;
  const isOpen = controlledOpen ?? open;
  return /* @__PURE__ */ jsx(Fade, { in: isOpen, appear: false, children: /* @__PURE__ */ jsxs(
    StyledAlert,
    {
      ref,
      severity,
      content,
      action,
      onClose: handleClose,
      hasCloseButton: Boolean(onClose),
      hasAction: Boolean(action),
      hasTitle: Boolean(title),
      isSticky,
      variant,
      "data-name": "alert",
      "data-sticky": isSticky,
      "data-content": content,
      ...otherProps,
      children: [
        title && /* @__PURE__ */ jsx(AlertTitle, { children: title }),
        /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "inherit", component: "div", children })
      ]
    }
  ) });
}
const Alert = forwardRef(_Alert);
export {
  Alert as A,
  Link as L,
  ScreenReaderOnly as S
};
